import type { MoneyDto } from '@proerp/shared';

const MICRO_PER_MAJOR = 1_000_000n;

/**
 * Regex für Geld-/Preiseingaben in Formularen: bis 12 Vorkomma-, bis **6**
 * Nachkommastellen (Punkt oder Komma). Bewusst identisch zur Präzision von
 * `decimalToMicros`/`microsToDecimalInput` — sonst lässt sich ein vorbefüllter
 * Sub-Cent-Preis (kg-/m-Preise, bis 6 Stellen) nicht mehr speichern, obwohl er
 * gültig ist. Als String-Regex nutzbar in Zod-Formularschemas.
 */
export const MONEY_INPUT_PATTERN = /^\d{1,12}([.,]\d{1,6})?$/;

/** Money-DTO → de-DE-Währungsformat, z. B. „125.000,50 €" (Guideline §10). */
export function formatMoney(value: MoneyDto): string {
  const micros = BigInt(value.amount);
  const euros = Number(micros) / Number(MICRO_PER_MAJOR);
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: value.currency }).format(
    euros,
  );
}

/**
 * Normalisiert eine deutsche Zahleneingabe auf Punkt-Dezimalform. Vollständige
 * Tausendergruppierung („1.500", „1.234.567,89") wird erkannt und entfernt —
 * sonst würde `Number("1.500")` still 1,5 statt 1500 liefern. Punkt-Dezimalen
 * („1.5") bleiben gültig; die Gruppierung greift nur, wenn das ganze Muster passt
 * (erste Gruppe 1–3 Ziffern ohne führende 0, dann Punkt + exakt 3er-Gruppen).
 */
export function normalizeDecimalInput(input: string): string {
  const trimmed = input.trim().replace(/\s/g, '');
  if (/^-?[1-9]\d{0,2}(\.\d{3})+(,\d+)?$/.test(trimmed)) {
    return trimmed.replace(/\./g, '').replace(',', '.');
  }
  return trimmed.replace(',', '.');
}

/**
 * Dezimaleingabe (z. B. „125000,50", „1.500" oder „125000.5") → Mikro-Einheiten als String.
 * Ganzzahl-Arithmetik, kein Float (CLAUDE.md §6). Gibt undefined bei leerer/ungültiger Eingabe.
 */
export function decimalToMicros(input: string): string | undefined {
  const normalized = normalizeDecimalInput(input);
  if (!normalized) return undefined;
  if (!/^-?\d+(\.\d{1,6})?$/.test(normalized)) return undefined;
  const isNegative = normalized.startsWith('-');
  const [intPart, fracPart = ''] = normalized.replace('-', '').split('.');
  const fracMicros = (fracPart + '000000').slice(0, 6);
  const micros = BigInt(intPart) * MICRO_PER_MAJOR + BigInt(fracMicros);
  return (isNegative ? -micros : micros).toString();
}

/**
 * Mikro-Einheiten → Eingabewert mit Komma (für Edit-Formular). Mindestens zwei
 * Nachkommastellen; Sub-Cent-Präzision (kg-/m-Preise, bis 6 Stellen) bleibt
 * erhalten statt still abgeschnitten zu werden.
 */
export function microsToDecimalInput(amount: string): string {
  const micros = BigInt(amount);
  const isNegative = micros < 0n;
  const abs = isNegative ? -micros : micros;
  const major = abs / MICRO_PER_MAJOR;
  const fraction = (abs % MICRO_PER_MAJOR).toString().padStart(6, '0').replace(/0+$/, '');
  const digits = fraction.length <= 2 ? fraction.padEnd(2, '0') : fraction;
  return `${isNegative ? '-' : ''}${major},${digits}`;
}
